import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  IMyBusinessFormModel,
  myBusinessFormDefaultValues,
  myBusinessFormSchema,
} from "../models/myBusinessFormModel";

export interface IMyBusinessData {
  companyName: string | null;
  bulstat: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  mol: string | null;
}

interface IUseMyBusinessFormProps {
  onSaved: () => void;
}

export const useMyBusinessForm = ({ onSaved }: IUseMyBusinessFormProps) => {
  const {
    data: business,
    isLoading,
    isFetching,
  } = useApiQuery<IMyBusinessData>({
    queryKey: [endpointsMetadata.myBusiness.getMyBusiness],
    endpoint: endpointsMetadata.myBusiness.getMyBusiness,
    globalIsLoading: false,
  });

  const form = useForm<IMyBusinessFormModel>({
    resolver: yupResolver(myBusinessFormSchema),
    defaultValues: myBusinessFormDefaultValues,
  });

  const hasExistingBusiness = !!(business && business.companyName);

  useEffect(() => {
    if (business) {
      form.reset({
        companyName: business.companyName ?? "",
        bulstat: business.bulstat ?? "",
        address: business.address ?? "",
        city: business.city ?? "",
        country: business.country ?? "България",
        mol: business.mol ?? "",
      });
    }
  }, [business]);

  const { mutate, isLoading: isSaving } = useApiMutation({
    onSuccess: () => {
      onSaved();
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    mutate({
      endpoint: endpointsMetadata.myBusiness.updateMyBusiness,
      method: "put",
      payload: {
        companyName: data.companyName.trim(),
        bulstat: data.bulstat.trim(),
        address: data.address.trim(),
        city: data.city.trim(),
        country: data.country.trim(),
        mol: data.mol.trim(),
      },
      successMessage: "Бизнес данните бяха запазени успешно.",
      invalidateKeys: [[endpointsMetadata.myBusiness.getMyBusiness]],
    });
  });

  return {
    form,
    business,
    hasExistingBusiness,
    isLoading: isLoading || isFetching,
    isSaving,
    onSubmit,
  };
};
